import React, { useState, useMemo } from 'react';
import { chat } from '../../styles/components';
import { cx } from '../../styles';
import { Message } from '../../utils/types';

interface MessageThreadProps {
  messages: Message[];
  loading: boolean;
  onRelatedQueryClick?: (query: string) => void;
  onSourceClick?: (index: number) => void;
}

const MessageThread: React.FC<MessageThreadProps> = ({
  messages,
  onSourceClick,
}) => {

  const citationMap = useMemo(() => {
    const map = new Map<string, number>();
    let globalIndex = 0;
    messages.forEach(message => {
      if (message.citations && message.citations.length > 0) {
        message.citations.forEach(citation => {
          if (!map.has(citation)) {
            map.set(citation, globalIndex++);
          }
        });
      }
    });
    return map;
  }, [messages]);

  const renderCompactCitations = (message: Message) => {
    if (!message.citations || message.citations.length === 0) return null;
    return (
      <div className="text-xs text-tertiary mt-2 flex flex-wrap items-center gap-1">
        <span>Sources:</span>
        {message.citations.map((citation, localIndex) => {
          const globalIndex = citationMap.get(citation) ?? localIndex;
          return (
            <button
              key={localIndex}
              onClick={() => onSourceClick && onSourceClick(localIndex)}
              className="text-accent hover:underline cursor-pointer"
            >
              [{globalIndex + 1}]
            </button>
          );
        })}
      </div>
    );
  };
  // --- End Citation Logic ---

  return (
    <div className={chat.messagesContainer}>
      {messages.map((message) => (
        // Each message turn container needs 'group' and 'relative' for the copy button
        <div
          key={message.id}
          className={cx(chat.messageTurn, 'group relative')}
        >
          {message.role === 'user' ? (
            // User Query Block - No sticky class applied
            <div
              // No ref needed
              className={cx("flex items-start gap-3")}
              data-query-id={message.id} // Keep for potential future debugging
            >
              {/* Placeholder for User Avatar */}
              {/* <div className="w-6 h-6 bg-blue-200 rounded-full flex-shrink-0 mt-1"></div> */}
              <div className="flex-grow">
                <div className={chat.queryTitle}>
                  {message.content}
                </div>
                 {/* Copy button positioned absolutely within the outer 'group relative' div */}
                 <CopyButton content={message.content} />
              </div>
            </div>
          ) : message.role === 'assistant' ? (
            // Assistant Response Block
            <div
              // No ref needed
              className="flex items-start gap-3"
            >
              {/* Placeholder for Assistant Avatar */}
              {/* <div className="w-6 h-6 bg-green-200 rounded-full flex-shrink-0 mt-1"></div> */}
              <div className={cx(chat.botContent, "flex-grow")}>
                <div
                  className={chat.botText}
                  dangerouslySetInnerHTML={{
                    __html: message.content // Content is sanitized by backend
                  }}
                />
                 {/* Copy button positioned absolutely within the outer 'group relative' div */}
                <CopyButton content={message.content} />
                {renderCompactCitations(message)}
              </div>
            </div>
          ) : null /* Handle system messages if needed, or ignore */}
        </div>
      ))}
    </div>
  );
};

// Copy button component (remains the same)
const CopyButton = ({ content }: { content: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={chat.copyButtonContainer}>
      <button
        onClick={handleCopy}
        className={chat.copyButton}
        aria-label="Copy message"
      >
        {copied ? (
          <span className={chat.copySuccess}>Copied!</span>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        )}
      </button>
    </div>
  );
};

export default MessageThread;
